import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { blogAdminAPI, filePublicUrl } from '../../services/api';
import RichTextEditor from '../../components/SuperAdmin/RichTextEditor';
import { ExportMenu, DateRangeFilter, FilterSelect, ResetFiltersButton, filterSelectClass } from '../../components/SuperAdmin/ListTools';
import {
  ContentPageHeader, ContentStats, ContentListCard, ContentEmpty, ContentThumb, ContentRow, MetaDot,
  ReorderButtons, LiveToggle, RowActions, ContentModal, Field, FilePicker, inputClass,
} from '../../components/SuperAdmin/ContentAdmin';
import { inDateRange, matchesSearch, formatExportDate } from '../../utils/exportData';
import toast from 'react-hot-toast';
import { HiOutlinePencilAlt } from 'react-icons/hi';

const formatShortDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

const Spinner = () => <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />;

const slugify = (value) =>
  String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const emptyForm = { title: '', slug: '', excerpt: '', author: '', category: '', body: '', coverImageCaption: '' };

const EMPTY_BLOG_FILTERS = { search: '', status: '', category: '', author: '', from: '', to: '' };

const BLOG_EXPORT_COLUMNS = [
  { header: 'Title', value: (p) => p.title },
  { header: 'Slug', value: (p) => p.slug },
  { header: 'Category', value: (p) => p.category },
  { header: 'Author', value: (p) => p.author },
  { header: 'Excerpt', value: (p) => p.excerpt },
  { header: 'Status', value: (p) => (p.isActive ? 'Live' : 'Hidden') },
  { header: 'Order', value: (p) => p.order },
  { header: 'Created', value: (p) => formatExportDate(p.createdAt) },
  { header: 'Updated', value: (p) => formatExportDate(p.updatedAt) },
];

const PostModal = ({ editing, onClose }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(
    editing
      ? { title: editing.title, slug: editing.slug, excerpt: editing.excerpt || '', author: editing.author || '', category: editing.category || '', body: editing.body || '', coverImageCaption: editing.coverImage?.caption || '' }
      : { ...emptyForm }
  );
  const [slugTouched, setSlugTouched] = useState(!!editing);
  const [coverFile, setCoverFile] = useState(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['superadmin-blog'] });

  const saveMutation = useMutation({
    mutationFn: () => {
      const formData = new FormData();
      formData.append('title', form.title.trim());
      formData.append('slug', form.slug.trim() || slugify(form.title));
      formData.append('excerpt', form.excerpt.trim());
      formData.append('author', form.author.trim());
      formData.append('category', form.category.trim());
      formData.append('body', form.body);
      formData.append('coverImageCaption', form.coverImageCaption.trim());
      if (coverFile) formData.append('coverImage', coverFile);
      return editing ? blogAdminAPI.update(editing._id, formData) : blogAdminAPI.create(formData);
    },
    onSuccess: () => {
      invalidate();
      toast.success(editing ? 'Post updated' : 'Post created');
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to save post'),
  });

  const handleTitleChange = (title) => {
    setForm((f) => ({ ...f, title, slug: slugTouched ? f.slug : slugify(title) }));
  };

  return (
    <ContentModal
      wide
      title={editing ? 'Edit post' : 'New post'}
      subtitle="Shown on the website's Blog page as soon as you save"
      onClose={onClose}
      onSave={() => saveMutation.mutate()}
      saving={saveMutation.isPending}
      canSave={!!form.title.trim()}
    >
      <Field label="Title" required>
        <input type="text" value={form.title} onChange={(e) => handleTitleChange(e.target.value)}
          placeholder="e.g. 10 telecalling tips that close more deals" className={inputClass} />
      </Field>
      <Field label="URL slug" hint={`Page address: /blog/${form.slug || slugify(form.title) || 'your-post'}`}>
        <input type="text" value={form.slug}
          onChange={(e) => { setSlugTouched(true); setForm({ ...form, slug: e.target.value }); }}
          placeholder="Filled in from the title" className={`${inputClass} font-mono`} />
      </Field>
      <Field label="Excerpt" hint="One or two sentences shown on the Blog list">
        <textarea rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
          placeholder="What is this post about?" className={inputClass} />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Author">
          <input type="text" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })}
            placeholder="e.g. TeleValley Team" className={inputClass} />
        </Field>
        <Field label="Category">
          <input type="text" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
            placeholder="e.g. Guides" className={inputClass} />
        </Field>
      </div>
      <Field label="Cover image" hint="A wide image (about 1200 × 630) works best">
        <FilePicker accept="image/*" file={coverFile} onChange={setCoverFile}
          currentUrl={editing?.coverImage?.url ? filePublicUrl(editing.coverImage.url) : null} />
      </Field>
      <Field label="Image caption" hint="Optional — shown under the cover image">
        <input type="text" value={form.coverImageCaption}
          onChange={(e) => setForm({ ...form, coverImageCaption: e.target.value })} className={inputClass} />
      </Field>
      <Field label="Article">
        <p className="text-xs text-gray-500 mb-2">
          Split the article into sections with <span className="font-semibold text-primary-600">+ Add section</span>.
          Each section heading becomes a jump-link in the &quot;In This Article&quot; list beside the post.
        </p>
        <RichTextEditor value={form.body} onChange={(body) => setForm({ ...form, body })} />
      </Field>
    </ContentModal>
  );
};

const BlogPosts = () => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['superadmin-blog'],
    queryFn: async () => (await blogAdminAPI.getAll()).data,
  });
  const posts = data?.data || [];
  const [filters, setFilters] = useState(EMPTY_BLOG_FILTERS);
  const setFilter = (patch) => setFilters((prev) => ({ ...prev, ...patch }));
  const filtersActive = Object.keys(EMPTY_BLOG_FILTERS).some((k) => filters[k]);
  const categories = [...new Set(posts.map((p) => p.category).filter(Boolean))].sort();
  const authors = [...new Set(posts.map((p) => p.author).filter(Boolean))].sort();
  const visiblePosts = posts.filter((p) =>
    matchesSearch(filters.search, p.title, p.slug, p.excerpt, p.author, p.category)
    && (!filters.status || (filters.status === 'live' ? p.isActive : !p.isActive))
    && (!filters.category || p.category === filters.category)
    && (!filters.author || p.author === filters.author)
    && inDateRange(p.createdAt, filters.from, filters.to));

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['superadmin-blog'] });

  const toggleMutation = useMutation({ mutationFn: (id) => blogAdminAPI.toggle(id), onSuccess: invalidate });
  const deleteMutation = useMutation({
    mutationFn: (id) => blogAdminAPI.delete(id),
    onSuccess: () => { invalidate(); toast.success('Post deleted'); },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to delete post'),
  });
  const reorderMutation = useMutation({
    mutationFn: (order) => blogAdminAPI.reorder(order),
    onSuccess: invalidate,
  });

  const move = (index, dir) => {
    const next = [...posts];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    reorderMutation.mutate(next.map((p) => p._id));
  };

  const openNew = () => { setEditing(null); setShowModal(true); };
  const openEdit = (p) => { setEditing(p); setShowModal(true); };

  return (
    <div className="p-6 space-y-6">
      <ContentPageHeader
        icon={HiOutlinePencilAlt}
        title="Blog"
        description="Articles on the website's Blog page. Changes go live as soon as you save."
        actionLabel="New Post"
        onAction={openNew}
      />

      {posts.length > 0 && <ContentStats items={posts} noun="posts" />}

      <ContentListCard
        filters={posts.length > 0 && (
          <>
            <input
              type="text"
              placeholder="Search title, slug, excerpt, author…"
              value={filters.search}
              onChange={(e) => setFilter({ search: e.target.value })}
              className={`${filterSelectClass} flex-1 sm:min-w-[240px]`}
            />
            <FilterSelect value={filters.status} onChange={(v) => setFilter({ status: v })}
              allLabel="All Status" options={[['live', 'Live'], ['hidden', 'Hidden']]} />
            {categories.length > 0 && (
              <FilterSelect value={filters.category} onChange={(v) => setFilter({ category: v })}
                allLabel="All Categories" options={categories.map((c) => [c, c])} />
            )}
            {authors.length > 0 && (
              <FilterSelect value={filters.author} onChange={(v) => setFilter({ author: v })}
                allLabel="All Authors" options={authors.map((a) => [a, a])} />
            )}
            <DateRangeFilter label="Created" from={filters.from} to={filters.to}
              onChange={({ from, to }) => setFilter({ from, to })} />
            <ResetFiltersButton active={filtersActive} onReset={() => setFilters(EMPTY_BLOG_FILTERS)} />
            <ExportMenu fileName="blog-posts" columns={BLOG_EXPORT_COLUMNS} getRows={() => visiblePosts} />
          </>
        )}
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-14"><Spinner /></div>
        ) : posts.length === 0 ? (
          <ContentEmpty icon={HiOutlinePencilAlt} title="No posts yet"
            hint="Write your first article to show it on the Blog page." actionLabel="New Post" onAction={openNew} />
        ) : visiblePosts.length === 0 ? (
          <ContentEmpty icon={HiOutlinePencilAlt} title="No posts match these filters" />
        ) : (
          <div className="divide-y divide-gray-100">
            {visiblePosts.map((p, i) => (
              <ContentRow
                key={p._id}
                isActive={p.isActive}
                // Reordering a filtered subset would scramble the real order, so only when unfiltered
                reorder={!filtersActive && (
                  <ReorderButtons isFirst={i === 0} isLast={i === posts.length - 1}
                    disabled={reorderMutation.isPending} onUp={() => move(i, -1)} onDown={() => move(i, 1)} />
                )}
                thumb={<ContentThumb wide icon={HiOutlinePencilAlt} src={p.coverImage?.url ? filePublicUrl(p.coverImage.url) : null} />}
                title={p.title}
                meta={(
                  <>
                    <span className="hidden sm:inline font-mono text-gray-400">/blog/{p.slug}</span>
                    {p.category && <><MetaDot className="hidden sm:inline" /><span className="px-1.5 py-0.5 rounded bg-primary-50 text-primary-700 font-medium">{p.category}</span></>}
                    {p.author && <><MetaDot /><span>{p.author}</span></>}
                    <MetaDot /><span>{formatShortDate(p.createdAt)}</span>
                  </>
                )}
                description={p.excerpt}
                controls={(
                  <>
                    <LiveToggle isActive={p.isActive} disabled={toggleMutation.isPending} onToggle={() => toggleMutation.mutate(p._id)} />
                    <RowActions name={p.title} onEdit={() => openEdit(p)} onDelete={() => deleteMutation.mutate(p._id)} />
                  </>
                )}
              />
            ))}
          </div>
        )}
      </ContentListCard>

      {showModal && <PostModal editing={editing} onClose={() => setShowModal(false)} />}
    </div>
  );
};

export default BlogPosts;
